import { useState, useEffect, useRef } from "react";
import { useAuth, apiClient, API } from "@/App";import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useConfirmation } from "@/components/ConfirmationProvider";
import { Plus, FolderOpen, Edit2, Trash2, FileText, Calendar, User, Download, File, Upload, X, Cloud } from "lucide-react";
import { format, parseISO } from "date-fns";
import axios from "axios";

const DOCUMENT_TYPES = [
  "Assessment Report", "IEP Document", "IFSP Document", "Progress Report",
  "Therapy Notes", "Medical Record", "School Record", "Insurance Document", "Other"
];

function formatFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export default function DocumentsPage() {
  const { token, user, refreshUser } = useAuth();
  const confirm = useConfirmation();
  const [documents, setDocuments] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ child_id: "", title: "", document_type: "", notes: "" });
  const [driveConnected, setDriveConnected] = useState(false);
  const [dropboxConnected, setDropboxConnected] = useState(false);

  useEffect(() => { fetchData(); checkCloudStatus(); }, []);

  const checkCloudStatus = async () => {
    try {
      const [driveRes, dropboxRes] = await Promise.all([
        apiClient.get("/drive/status"),
        apiClient.get("/dropbox/status")
      ]);
      setDriveConnected(driveRes.data.connected);
      setDropboxConnected(dropboxRes.data.connected);
    } catch {}
  };

  const fetchData = async () => {
    try {
      const [docsRes, childrenRes] = await Promise.all([
        apiClient.get("/documents"),
        apiClient.get("/children")
      ]);
      setDocuments(docsRes.data);
      setChildren(childrenRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const maxMb = user?.limits?.max_document_size_mb ?? 50;
      if (file.size > maxMb * 1024 * 1024) {
        toast.error(`File too large. ${user?.subscription_tier === 'free' ? `Free plan limit is ${maxMb} MB — Premium coming soon for larger files.` : `Maximum size is ${maxMb} MB.`}`);
        return;
      }
      setSelectedFile(file);
      if (!form.title) setForm(prev => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, "") }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDoc) {
        await apiClient.put(`/documents/${editingDoc.id}`, form);
        toast.success("Document updated!");
      } else if (selectedFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("child_id", form.child_id);
        formData.append("title", form.title);
        formData.append("document_type", form.document_type);
        formData.append("notes", form.notes);
        await axios.post(`${API}/documents/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
        });
        const synced = [driveConnected && "Google Drive", dropboxConnected && "Dropbox"].filter(Boolean);
        toast.success(synced.length ? `Document uploaded! Auto-syncing to ${synced.join(" & ")}.` : "Document uploaded!");
      } else {
        await apiClient.post("/documents", form);
        toast.success("Document added!");
      }
      fetchData();
      refreshUser();
      resetForm();
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to save";
      const code = error.response?.data?.code;
      if (code === 'tier_limit_documents' || code === 'tier_limit_document_size') {
        toast.error(detail, { duration: 8000 });
      } else {
        toast.error(detail);
      }
    }
    setUploading(false);
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || doc.title;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: "Delete document?", description: "This document will also be removed from connected cloud folders." }))) return;
    try {
      await apiClient.delete(`/documents/${id}`);
      toast.success("Document deleted");
      fetchData();
      refreshUser();
    } catch { toast.error("Failed to delete"); }
  };

  const openEdit = (doc) => {
    setEditingDoc(doc);
    setForm({ child_id: doc.child_id, title: doc.title, document_type: doc.document_type, notes: doc.notes || "" });
    setSelectedFile(null);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({ child_id: "", title: "", document_type: "", notes: "" });
    setEditingDoc(null);
    setSelectedFile(null);
    setDialogOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const getChildName = (id) => children.find(c => c.id === id)?.name || "Unknown";

  const getDocTypeColor = (type) => {
    const colors = {
      "Assessment Report": "bg-teal-100 text-teal-700",
      "IEP Document": "bg-golden-100 text-golden-500",
      "IFSP Document": "bg-coral-100 text-coral-500",
      "Progress Report": "bg-success/10 text-success",
      "Therapy Notes": "bg-info/10 text-info"
    };
    return colors[type] || "bg-muted text-muted-foreground";
  };

  const getDocIcon = (type) => {
    if (type?.includes("Report")) return FileText;
    if (type?.includes("IEP") || type?.includes("IFSP")) return File;
    return FolderOpen;
  };

  const groupedDocs = documents.reduce((acc, doc) => {
    const childId = doc.child_id || "general";
    if (!acc[childId]) acc[childId] = [];
    acc[childId].push(doc);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 bg-muted rounded-lg w-48" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-36 bg-muted rounded-3xl" />)}
        </div>
      </div>
    );
  }

  const syncTargets = [driveConnected && "Google Drive", dropboxConnected && "Dropbox"].filter(Boolean);
  const isFree = user?.subscription_tier === 'free';
  const docLimit = user?.limits?.max_documents;
  const docUsage = user?.usage?.documents ?? 0;
  const atDocLimit = isFree && docLimit && docUsage >= docLimit;

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
            <FolderOpen className="h-8 w-8" />
            Documents
          </h1>
          <p className="text-muted-foreground mt-1">Upload and organize important documents</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isFree && docLimit && (
            <span
              className={`text-xs font-medium px-3 py-1.5 rounded-full ${
                atDocLimit ? 'bg-coral-500/10 text-coral-500' : 'bg-muted text-muted-foreground'
              }`}
              data-testid="documents-usage-badge"
            >
              {docUsage} / {docLimit} on Free
            </span>
          )}

          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button
                className="rounded-full"
                data-testid="add-document-btn"
                disabled={atDocLimit}
                title={atDocLimit ? 'Free plan limit reached — Premium coming soon' : ''}
              >
                <Plus className="mr-2 h-4 w-4" />
                Upload Document
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-lg rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingDoc ? "Edit Document" : "Upload Document"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* File Upload */}
              {!editingDoc && (
                <div className="space-y-2">
                  <Label>File</Label>
                  <div
                    className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="file-dropzone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.xlsx,.csv"
                    />
                    {selectedFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <File className="h-8 w-8 text-primary" />
                        <div className="text-left">
                          <p className="font-medium text-sm">{selectedFile.name}</p>
                          <p className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={(e) => { e.stopPropagation(); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                        <p className="text-sm text-muted-foreground">Click to select a file</p>
                        <p className="text-xs text-muted-foreground/70 mt-1">PDF, DOC, DOCX, images, spreadsheets (max {user?.limits?.max_document_size_mb ?? 50} MB)</p>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Child</Label>
                <Select value={form.child_id} onValueChange={(v) => setForm({ ...form, child_id: v })} required>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select child" /></SelectTrigger>
                  <SelectContent>
                    {children.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Document Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Annual Assessment 2024"
                  required
                  className="rounded-xl"
                  data-testid="document-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Document Type</Label>
                <Select value={form.document_type} onValueChange={(v) => setForm({ ...form, document_type: v })} required>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Additional notes..."
                  className="rounded-xl resize-none"
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1 rounded-full">Cancel</Button>
                <Button type="submit" className="flex-1 rounded-full" disabled={uploading} data-testid="save-document-btn">
                  {uploading ? "Uploading..." : editingDoc ? "Update" : selectedFile ? "Upload" : "Add"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {syncTargets.length > 0 && (
        <div
          className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-primary/5 border border-primary/15 text-sm text-primary/90"
          data-testid="auto-sync-indicator"
        >
          <Cloud className="h-4 w-4" />
          <span>
            Auto-syncing new documents to <span className="font-semibold">{syncTargets.join(" & ")}</span> in a dedicated <code className="text-xs bg-primary/10 px-1.5 py-0.5 rounded">Thrively Backup</code> folder.
          </span>
        </div>
      )}

      {documents.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="py-16 text-center">
            <FolderOpen className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">No Documents Yet</h3>
            <p className="text-muted-foreground mb-6">Upload important therapy and medical documents</p>
            <Button onClick={() => setDialogOpen(true)} className="rounded-full">
              <Plus className="mr-2 h-4 w-4" /> Upload Document
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedDocs).map(([childId, docs]) => (
            <div key={childId}>
              <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                {childId === "general" ? "General" : getChildName(childId)}'s Documents
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {docs.map((doc, i) => {
                  const DocIcon = getDocIcon(doc.document_type);
                  return (
                    <Card
                      key={doc.id}
                      className={`border-0 shadow-lg rounded-3xl card-hover animate-fadeIn stagger-${(i % 5) + 1}`}
                      data-testid={`document-card-${doc.id}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <DocIcon className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-bold line-clamp-1 mb-1">{doc.title}</h3>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={`rounded-full text-xs ${getDocTypeColor(doc.document_type)}`}>
                                {doc.document_type}
                              </Badge>
                              {doc.file_size && (
                                <span className="text-xs text-muted-foreground">{formatFileSize(doc.file_size)}</span>
                              )}
                            </div>
                            {doc.original_filename && (
                              <p className="text-xs text-muted-foreground mt-1 font-mono truncate">{doc.original_filename}</p>
                            )}
                            {doc.notes && (
                              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{doc.notes}</p>
                            )}
                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {format(parseISO(doc.created_at), "MMM d, yyyy")}
                              </p>
                              {(doc.drive_file_id || doc.dropbox_path) && (
                                <span
                                  className="text-xs text-primary/80 flex items-center gap-1"
                                  data-testid={`synced-badge-${doc.id}`}
                                  title={`Synced to ${[doc.drive_file_id && 'Drive', doc.dropbox_path && 'Dropbox'].filter(Boolean).join(' & ')}`}
                                >
                                  <Cloud className="h-3 w-3" /> Synced
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end gap-1 mt-4 pt-4 border-t border-border">
                          {doc.storage_key && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(doc)}
                              className="rounded-full"
                              data-testid={`download-btn-${doc.id}`}
                            >
                              <Download className="h-4 w-4 mr-1" /> Download
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => openEdit(doc)} className="rounded-full h-9 w-9">
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(doc.id)} className="rounded-full h-9 w-9 text-destructive hover:text-destructive" data-testid={`delete-btn-${doc.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
